export type PersonOption = {
  id?: string|number
  value?: string|number
  label?: string
  full_name?: string
  name?: string
  phone?: string
  mobile?: string
}

export type ReadOnlyField = {
  key: string
  label: string
  value?: any
  kind?: 'text'|'boolean'|'array'|'object'|'table'
  rows?: Record<string, any>[]
  columns?: Array<{ key: string, label: string }>
}

export type ReadOnlySection = {
  key: string
  title: string
  fields?: ReadOnlyField[]
  tables?: ReadOnlyField[]
}

export function valueOrDash (value: any): string {
  if (value === null || typeof value === 'undefined' || value === '') return '-'
  if (Array.isArray(value)) return value.length ? value.map(valueOrDash).filter(item => item !== '-').join(', ') || '-' : '-'
  if (typeof value === 'boolean') return booleanLabel(value)
  if (value instanceof Date) return dateLabel(value)
  if (typeof value === 'object') {
    if (value.label || value.full_name || value.name) return value.label || value.full_name || value.name
    return Object.keys(value)
      .map(key => `${labelFor(key)}: ${valueOrDash(value[key])}`)
      .filter(item => !item.endsWith(': -'))
      .join(', ') || '-'
  }
  return String(value)
}

export function booleanLabel (value: any): string {
  if (value === true || value === 1 || value === '1') return 'Ja'
  if (value === false || value === 0 || value === '0') return 'Nee'
  return '-'
}

export function dateLabel (value: any, includeTime = false): string {
  if (!value) return '-'
  const date = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return valueOrDash(value)
  return date.toLocaleString('nl-NL', includeTime
    ? { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
    : { year: 'numeric', month: '2-digit', day: '2-digit' })
}

export function personLabel (value: any, people: PersonOption[] = []): string {
  if (value === null || typeof value === 'undefined' || value === '') return '-'
  if (typeof value === 'object') return valueOrDash(value)
  const id = String(value)
  const person = people.find(option => String(option.id ?? option.value) === id)
  return person ? String(person.label || person.full_name || person.name || id) : id
}

export function listLabels (values: any, people: PersonOption[] = []): string[] {
  if (!Array.isArray(values)) return []
  return values
    .map(value => people.length ? personLabel(value, people) : valueOrDash(value))
    .filter(value => value !== '-')
}

export function checkedItemsFromBooleanMap (value: any, labels: Record<string, string>): string[] {
  if (Array.isArray(value)) {
    return value.map(item => labels[String(item)] || valueOrDash(item)).filter(item => item !== '-')
  }
  if (!value || typeof value !== 'object') return []
  return Object.keys(labels).filter(key => Boolean(value[key])).map(key => labels[key])
}

export function labelFor (key: string): string {
  const labels: Record<string, string> = {
    siteManagerId: 'Uitvoerder',
    projectManagerId: 'Projectleider',
    dateTimeOfIncident: 'Datum van incident',
    incidentDescription: 'Wat is er gebeurd?',
    incidentLocation: 'Waar is het gebeurd?',
    woononInvolvedParties: 'Betrokken partijen',
    nonWoononInvolvedParties: 'Externe betrokken partijen',
    woononWitnesses: 'Getuigen',
    nonWoononWitnesses: 'Externe getuigen',
    injuries: 'Is er letsel?',
    injuryDescription: 'Wat is het letsel?',
    materialDamage: 'Is er materiaalschade?',
    materialDamageDescription: 'Omschrijving van materiaalschade',
    environmentalIncident: 'Is er een milieu-incident?',
    environmentalIncidentDescription: 'Toelichting milieu-incident',
    dangerousSituation: 'Was er een gevaarlijke situatie?',
    dangerousSituationDescription: 'Toelichting gevaarlijke situatie',
    resultOfAccident: 'Gevolg van arbeidsongeval',
    referenceNLA: 'Meldingsnummer NLA',
    notificationDateNLA: 'Datum melding NLA',
    kamManagerId: 'Contactpersoon intern'
  }
  if (labels[key]) return labels[key]
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^./, c => c.toUpperCase())
}
